import React, { useState, useEffect, useRef } from 'react';
import { motion, useAnimation, useInView } from 'framer-motion';
import { HiArrowRight, HiCode, HiLightningBolt, HiSparkles } from 'react-icons/hi';
import XboxExploitWidget from './XboxExploitWidget';
import StormDashWidget from './StormDashWidget';
import ScrollReveal from './ScrollReveal';
import LiquidEther from './LiquidEther';

const Hero = () => {
  const [currentWord, setCurrentWord] = useState('INNOVATIVE');
  const [isMobile, setIsMobile] = useState(false);
  const heroRef = useRef(null);
  const isInView = useInView(heroRef, { once: true });
  const controls = useAnimation();

  useEffect(() => {
    if (isInView) {
      controls.start('visible');
    }
  }, [isInView, controls]);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    const words = ['INNOVATIVE', 'CREATIVE', 'GROUNDBREAKING', 'REVOLUTIONARY'];
    const interval = setInterval(() => {
      setCurrentWord(prev => {
        const currentIndex = words.indexOf(prev);
        return words[(currentIndex + 1) % words.length];
      });
    }, 3000);

    return () => clearInterval(interval);
  }, []);


  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100
      }
    }
  };

  return (
    <section id="home" ref={heroRef} className="relative min-h-screen flex items-center justify-center overflow-hidden scroll-mt-20">
      {/* Background */}
      <div className="fixed inset-0 z-0 bg-black">
        {!isMobile && (
          <LiquidEther
            colors={['#ffffff', '#9ca3af', '#6b7280']}
            mouseForce={25}
            cursorSize={120}
            isViscous={false}
            viscous={30}
            iterationsViscous={32}
            iterationsPoisson={32}
            resolution={0.5}
            isBounce={false}
            autoDemo={true}
            autoSpeed={0.8}
            autoIntensity={3.5}
            takeoverDuration={0.3}
            autoResumeDelay={1000}
            autoRampDuration={0.8}
            style={{ width: '100vw', height: '100vh', position: 'fixed', top: 0, left: 0 }}
          />
        )}
      </div>

      {/* Main Content */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate={controls}
        className="relative z-20 text-center md:text-center text-left max-w-6xl mx-auto px-4 sm:px-6 md:px-8 flex flex-col items-center md:items-center items-start justify-center min-h-screen pt-20 sm:pt-24 pointer-events-none"
      >
        {/* Subtitle */}
        <motion.div
          variants={itemVariants}
          className="mb-6"
        >
          <div className="inline-flex items-center space-x-2 glass-morphism px-4 sm:px-6 md:px-8 py-2 md:py-3 rounded-full">
            <HiSparkles className="text-accent-green text-lg md:text-xl" />
            <span className="font-jetbrains text-accent-green tracking-wider text-xs md:text-sm">
              NEXT-GEN DEVELOPER & AI INNOVATOR
            </span>
            <HiSparkles className="text-accent-green text-lg md:text-xl" />
          </div>
        </motion.div>

        {/* Main Headline */}
        <motion.div variants={itemVariants} className="mb-8">
          <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-display-1 font-space-grotesk font-black leading-none mb-4 text-left md:text-center">
            <span className="block text-white">BUILDING</span>
            <span className="block relative" style={{ minHeight: '1em' }}>
              {/* Animated word */}
              <motion.span
                key={currentWord}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
                className="block gradient-text-green absolute left-0 top-0 w-full"
                style={{ minWidth: 0 }}
              >
                {currentWord}
              </motion.span>
              {/* Ghost word for width reservation */}
              <span className="opacity-0 pointer-events-none block">
                GROUNDBREAKING
              </span>
            </span>
            <span className="block text-white">DIGITAL</span>
            <span className="block metallic-text">EXPERIENCES</span>
          </h1>
        </motion.div>

        {/* Description */}
        <div className="max-w-6xl mx-auto mb-6 sm:mb-8 md:mb-12 px-2 sm:px-0">
          <ScrollReveal
            baseOpacity={0}
            enableBlur={true}
            baseRotation={5}
            blurStrength={10}
            containerClassName="text-center"
            textClassName="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-300 leading-relaxed text-center"
          >
            Student Software Developer crafting exceptional digital experiences that transform brands 
            and captivate audiences. I'm the strategic partner that brings your vision to life through 
            cutting-edge technology and innovative design.
          </ScrollReveal>
        </div>

        {/* CTA Buttons */}
        <motion.div
          variants={itemVariants}
          className="flex flex-col sm:flex-row gap-4 md:gap-6 justify-center md:justify-center items-center mb-8 sm:mb-12 md:mb-16 w-full md:w-auto px-4 sm:px-0"
        >
          <motion.button
            whileHover={{ scale: 1.05, boxShadow: "0 20px 60px rgba(156, 163, 175, 0.3)" }}
            whileTap={{ scale: 0.95 }}
            onClick={() => scrollToSection('projects')}
            className="group relative overflow-hidden btn-primary text-base md:text-lg px-6 md:px-8 py-3 md:py-4 flex items-center space-x-3 w-full sm:w-auto justify-center pointer-events-auto"
          >
            <span>Start a Project</span>
            <HiArrowRight className="text-xl transition-transform group-hover:translate-x-1" />
            <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity" />
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => scrollToSection('about')}
            className="group btn-secondary text-base md:text-lg px-6 md:px-8 py-3 md:py-4 flex items-center space-x-3 w-full sm:w-auto justify-center pointer-events-auto"
          >
            <span>Learn More</span>
            <HiCode className="text-xl transition-transform group-hover:rotate-12" />
          </motion.button>
        </motion.div>

        {/* Stats */}
        <motion.div
          variants={itemVariants}
          className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8 max-w-4xl mx-auto w-full px-4 sm:px-0"
        >
          {[
            { number: '15+', label: 'Projects Completed', icon: HiCode },
            { number: '3+', label: 'Years Experience', icon: HiLightningBolt },
            { number: '100%', label: 'Client Satisfaction', icon: HiSparkles }
          ].map((stat, index) => (
            <motion.div
              key={index}
              whileHover={{ y: -5, scale: 1.05 }}
              className="glass-morphism rounded-2xl p-4 sm:p-6 text-center group cursor-pointer pointer-events-auto"
            >
              <stat.icon className="text-2xl sm:text-3xl text-accent-green mx-auto mb-3 group-hover:scale-110 transition-transform" />
              <div className="text-2xl sm:text-3xl font-bold gradient-text-green mb-2">{stat.number}</div>
              <div className="text-gray-300 font-medium text-sm sm:text-base">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>

        {/* Xbox Exploit Widget */}
        <motion.div
          variants={itemVariants}
          className="mt-8 sm:mt-12 md:mt-16 px-4 sm:px-0 pointer-events-auto"
        >
          <XboxExploitWidget />
        </motion.div>

        {/* Storm Dash Widget */}
        <motion.div
          variants={itemVariants}
          className="mt-8 sm:mt-12 md:mt-16 px-4 sm:px-0 pointer-events-auto"
        >
          <StormDashWidget />
        </motion.div>


      </motion.div>

      {/* Side Elements - Positioned Above Main Headline */}
      <div className="absolute left-0 top-48 hidden lg:block">
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.5 }}
          className="flex flex-col space-y-6"
        >
          <div className="w-1 h-20 bg-green-gradient" />
          <div className="transform -rotate-90 whitespace-nowrap -translate-x-8">
            <span className="text-sm font-jetbrains tracking-[0.3em] text-accent-green">
              STUDENT DEVELOPER
            </span>
          </div>
        </motion.div>
      </div>

      <div className="absolute right-0 top-48 hidden lg:block">
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.5 }}
          className="flex flex-col space-y-6 items-end"
        >
          <div className="w-1 h-20 bg-blue-gradient" />
          <div className="transform rotate-90 whitespace-nowrap translate-x-8">
            <span className="text-sm font-jetbrains tracking-[0.3em] text-accent-blue">
              AI INNOVATOR
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;